import { useMutation, UseQueryOptions, useQuery, useQueryClient } from 'react-query'
import {
  CreateCoupon,
  CreateOfferingDiscountRequest,
  ResponseError,
  ShowCoupon,
  ShowOfferingDiscount,
  CouponsApi as TsCouponsApi,
  UpdateCoupon,
  UpdateOfferingDiscountRequest,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const tsApi = new TsCouponsApi(apiConfiguration)

export const useGetCoupon = (
  couponId: string,
  options: UseQueryOptions<ShowCoupon, ResponseError> = {}
) => {
  return useQuery<ShowCoupon, ResponseError>(
    ['coupon', { couponId }],
    () => tsApi.getCoupon({ couponId }),
    options
  )
}

export const useCreateCoupon = () => {
  const siteId = useCurrentSiteId()
  const queryClient = useQueryClient()

  return useMutation(
    (createCoupon: CreateCoupon) =>
      tsApi.createCoupon({ createCoupon: { ...createCoupon, site_id: siteId } }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('coupons')
      },
    }
  )
}

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ couponId, updateCoupon }: { couponId: string; updateCoupon: UpdateCoupon }) => {
      return tsApi.updateCoupon({ couponId, updateCoupon })
    },
    {
      onSuccess: (_, { couponId }) => {
        queryClient.invalidateQueries(['coupon', { couponId }])
      },
    }
  )
}

export const useCreateOfferingDiscount = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowOfferingDiscount, ResponseError, CreateOfferingDiscountRequest>(
    ({ couponId, createOfferingDiscount }) => {
      return tsApi.createOfferingDiscount({ couponId, createOfferingDiscount })
    },
    {
      onSuccess: (_, { couponId }) => {
        queryClient.invalidateQueries(['coupon', { couponId }])
      },
    }
  )
}

export const useUpdateOfferingDiscount = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowOfferingDiscount, ResponseError, UpdateOfferingDiscountRequest>(
    ({ couponId, offeringDiscountId, updateOfferingDiscount }) => {
      return tsApi.updateOfferingDiscount({
        couponId,
        offeringDiscountId,
        updateOfferingDiscount,
      })
    },
    {
      onSuccess: (_, { couponId }) => {
        queryClient.invalidateQueries(['coupon', { couponId }])
      },
    }
  )
}
